import {
  ConnectionTypeEnum,
  EICStatusEnum,
  RequestStatusEnum,
} from "../Premises/types";
import { Company } from "../Projects/types";
import {
  AccountPayer,
  GasOptionEnum,
  RequestingCompany,
  Service,
} from "./types";

export const formatDpNumber = (dpNumber: string | undefined, state: string) => {
  if (!dpNumber || !state) {
    return "";
  }
  if (state === "NSW" || state === "ACT") {
    return dpNumber.startsWith("DP") ? `${dpNumber}, ` : `DP${dpNumber}, `;
  }
  return dpNumber;
};

export const getIsNmiRequired = (postcode: string) =>
  [
    "2000",
    "2002",
    "2003",
    "2005",
    "2006",
    "2007",
    "2008",
    "2009",
    "2010",
    "2011",
    "2012",
    "2013",
    "2014",
    "2015",
    "2016",
    "2017",
    "2018",
    "2019",
    "2020",
    "2021",
    "2022",
    "2023",
    "2024",
    "2025",
    "2026",
    "2027",
    "2028",
    "2029",
    "2030",
    "2031",
    "2032",
    "2033",
    "2034",
    "2035",
    "2036",
    "2037",
    "2038",
    "2039",
    "2040",
    "2041",
    "2042",
    "2043",
    "2044",
    "2045",
    "2046",
    "2047",
    "2048",
    "2049",
    "2050",
    "2051",
    "2052",
    "2053",
    "2055",
    "2056",
    "2057",
    "2058",
    "2059",
    "2060",
    "2061",
    "2062",
    "2063",
    "2064",
    "2065",
    "2066",
    "2067",
    "2068",
    "2069",
    "2070",
    "2071",
    "2072",
    "2073",
    "2074",
    "2075",
    "2076",
    "2077",
    "2079",
    "2080",
    "2081",
    "2082",
    "2083",
    "2084",
    "2085",
    "2086",
    "2087",
    "2088",
    "2089",
    "2090",
    "2091",
    "2092",
    "2093",
    "2094",
    "2095",
    "2096",
    "2097",
    "2099",
    "2100",
    "2101",
    "2102",
    "2103",
    "2104",
    "2105",
    "2106",
    "2107",
    "2108",
    "2109",
    "2110",
    "2111",
    "2112",
    "2113",
    "2120",
    "2121",
    "2123",
    "2124",
    "2127",
    "2129",
    "2130",
    "2131",
    "2132",
    "2133",
    "2134",
    "2135",
    "2136",
    "2137",
    "2138",
    "2139",
    "2140",
    "2141",
    "2143",
    "2144",
    "2159",
    "2190",
    "2191",
    "2192",
    "2193",
    "2194",
    "2195",
    "2196",
    "2197",
    "2199",
    "2200",
    "2201",
    "2202",
    "2203",
    "2204",
    "2205",
    "2206",
    "2207",
    "2208",
    "2209",
    "2210",
    "2211",
    "2212",
    "2213",
    "2214",
    "2215",
    "2216",
    "2217",
    "2218",
    "2219",
    "2220",
    "2221",
    "2222",
    "2223",
    "2224",
    "2225",
    "2226",
    "2227",
    "2228",
    "2229",
    "2230",
    "2231",
    "2232",
    "2233",
    "2234",
    "2250",
    "2251",
    "2252",
    "2256",
    "2257",
    "2258",
    "2259",
    "2260",
    "2261",
    "2262",
    "2263",
    "2264",
    "2265",
    "2267",
    "2278",
    "2280",
    "2281",
    "2283",
    "2284",
    "2285",
    "2286",
    "2287",
    "2289",
    "2290",
    "2291",
    "2292",
    "2293",
    "2294",
    "2295",
    "2296",
    "2297",
    "2298",
    "2299",
    "2300",
    "2302",
    "2303",
    "2304",
    "2305",
    "2306",
    "2307",
    "2308",
    "2314",
    "2320",
    "2321",
    "2335",
    "2764",
  ].includes(postcode);

export const mapConnectionTypeToBlockName = (
  connectionType: ConnectionTypeEnum
) => {
  switch (connectionType) {
    case ConnectionTypeEnum.SERVICE_CONNECTION:
      return GasOptionEnum.GAS_LINE;
    case ConnectionTypeEnum.METER_FIX:
      return GasOptionEnum.GAS_METER;
    case ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX:
      return GasOptionEnum.BOTH;
    default:
      return GasOptionEnum.GAS_LINE;
  }
};

export const getCommSentDate = (services: Service[]) => {
  return services.find((s) => s.comm_sent_date_time)?.comm_sent_date_time;
};

export const getGasServicesContext = (services: Service[]) => {
  const gasServices = services.filter((s: Service) => s.service_type === "GAS");
  const gasServicesContext =
    gasServices.length > 0 &&
    gasServices.reduce((acc: any, g: any) => {
      return {
        ...acc,
        [mapConnectionTypeToBlockName(g.connection_type)]: {
          draftId: g.draft_id,
          draftVersion: g.draft_version,
          requestId: g.request_id,
          isReadOnly:
            g.raw_request_status !== RequestStatusEnum.DRAFT &&
            g.raw_request_status !== RequestStatusEnum.DRAFT_ASSIGNED,
          connectionType: g.connection_type,
          requestStatus: g.request_status,
          rawRequestStatus: g.raw_request_status,
          journeyId: g.journey_id,
          messagePause: g.message_pause,
          requestType: g.request_type,
          eicStatus: g.eic_status ?? EICStatusEnum.EIC_COMM_UNUSED,
          autoSubmit: g.auto_submit,
        },
      };
    }, {});

  return gasServicesContext;
};

export const getElectricityServiceContext = (electricityService: Service) =>
  electricityService
    ? {
        draftId: electricityService?.draft_id,
        draftVersion: electricityService?.draft_version,
        requestId: electricityService?.request_id,
        channel: electricityService?.channel,
        isReadOnly:
          electricityService?.channel === "CLAIM" ||
          (electricityService?.raw_request_status !== RequestStatusEnum.DRAFT &&
            electricityService?.raw_request_status !==
              RequestStatusEnum.DRAFT_ASSIGNED),
        connectionType: electricityService?.connection_type,
        requestStatus: electricityService?.request_status,
        rawRequestStatus: electricityService?.raw_request_status,
        journeyId: electricityService?.journey_id,
        messagePause: electricityService?.message_pause,
        requestType: electricityService?.request_type,
        distributorApplicationId:
          electricityService?.distributor_application_id,
        eicStatus:
          electricityService?.eic_status ?? EICStatusEnum.EIC_COMM_UNUSED,
        autoSubmit: electricityService?.auto_submit,
      }
    : undefined;

export const getInternetServiceContext = (internetService: Service) =>
  internetService
    ? {
        draftId: internetService?.draft_id,
        draftVersion: internetService?.draft_version,
        requestId: internetService?.request_id,
        channel: internetService?.channel,
        isReadOnly:
          internetService.connection_type === "CAN_RETAIL"
            ? internetService?.request_status !== RequestStatusEnum.DRAFT &&
              internetService?.request_status !== RequestStatusEnum.IN_PROGRESS
            : internetService.connection_type === "PRE_INSTALL_NBN"
            ? internetService?.request_status !== RequestStatusEnum.DRAFT
            : internetService.connection_type === "ADDRESS_NOT_FOUND"
            ? internetService?.request_status === "CANCELLED"
            : false,
        connectionType: internetService?.connection_type,
        requestStatus: internetService?.request_status,
        rawRequestStatus: internetService?.raw_request_status,
        journeyId: internetService?.journey_id,
        messagePause: internetService?.message_pause,
        messageEnabled: internetService?.message_enabled,
        requestType: internetService?.request_type,
        eicStatus:
          internetService?.eic_status ?? EICStatusEnum.EIC_COMM_NOT_SENT,
        handoverDate: internetService.handover_date,
        preinstallationDate: internetService.preinstallation_date,
        commSentDateTime: internetService.comm_sent_date_time,
      }
    : undefined;

export const getIsAccountReadOnly = (
  services: Service[],
  isCreatedByHomeUser: boolean
) => {
  return services.some(
    (s) =>
      s.raw_request_status !== RequestStatusEnum.DRAFT &&
      s.raw_request_status !== RequestStatusEnum.DRAFT_ASSIGNED &&
      (s.account_payer === AccountPayer.SOMEONE_ELSE || isCreatedByHomeUser)
  );
};

export const getIsHomeUserAccountReadOnly = (services: Service[]) => {
  return services.some(
    (s) =>
      s.raw_request_status !== RequestStatusEnum.DRAFT &&
      s.raw_request_status !== RequestStatusEnum.DRAFT_ASSIGNED
  );
};

export const fromCompanyToRequestingCompany = (
  company: Company | undefined
): RequestingCompany | undefined => {
  if (company === undefined) {
    return undefined;
  }
  return {
    msaNumber: company.msa_number,
    name: company.name,
    id: company.id,
    operationalTeam: company.operational_team,
  };
};

export const getIsNSWBorderPostcode = (postcode: string) =>
  [
    "2640",
    "2641",
    "2642",
    "2643",
    "2647",
    "2712",
    "2713",
    "2714",
    "2731",
  ].includes(postcode);
